import { Contents } from 'epubjs';

const ranges = new Map<string, (event: MouseEvent) => void>();
const elements = new Map<Element, () => void>();
const noMatchCallbacks: Array<() => void> = [];

const useClickHandler = () => {
  const handleClick = (contents: Contents, event: MouseEvent) => {
    // Bail while the user is actively dragging to select text.
    // We intentionally do NOT bail on isSelectingText here — that would block
    // both annotation clicks (context menu) and the "click outside to dismiss toolbar" path.
    const sel = contents.window.getSelection();
    if (sel && !sel.isCollapsed) return;

    for (const [cfi, callback] of ranges.entries()) {
      try {
        const range = contents.range(cfi);
        if (range) {
          const rects = range.getClientRects() || [];
          for (let i = 0; i < rects.length; i += 1) {
            const rect = rects[i];
            if (
              event.clientX >= rect.left &&
              event.clientX <= rect.right &&
              event.clientY >= rect.top &&
              event.clientY <= rect.bottom
            ) {
              callback(event);
              event.stopPropagation();
              return;
            }
          }
        }
      } catch {
        // CFI not on this page
      }
    }

    // Walk up the DOM tree to find an element with an ID
    let targetElement = event.target as HTMLElement;
    while (targetElement) {
      if (targetElement.id) {
        // Match by ID instead of object reference (handles stale iframe references)
        for (const [element, callback] of elements.entries()) {
          if (element.id === targetElement.id) {
            callback();
            event.stopPropagation();
            noMatchCallbacks.forEach((cb) => cb());
            return;
          }
        }
      }
      targetElement = targetElement.parentElement as HTMLElement;
    }

    // Click landed on nothing — notify all registered callbacks
    noMatchCallbacks.forEach((cb) => cb());
  };

  const registerContents = (contents: Contents) => {
    // Capture phase fires before any bubble-phase stopPropagation (e.g. audio annotation handlers).
    contents.document.addEventListener('click', (event: MouseEvent) => handleClick(contents, event), true);
  };

  const registerEpubCfiRange = (cfi: string, callback: (event: MouseEvent) => void) => {
    ranges.set(cfi, callback);
  };

  const unregisterEpubCfiRange = (cfi: string) => {
    ranges.delete(cfi);
  };

  const registerElement = (element: Element, callback: () => void) => {
    elements.set(element, callback);
  };

  const unregisterAllElements = () => {
    elements.clear();
  };

  const registerNoMatchCallback = (cb: () => void) => {
    noMatchCallbacks.push(cb);
  };

  const unregisterNoMatchCallback = (cb: () => void) => {
    const idx = noMatchCallbacks.indexOf(cb);
    if (idx !== -1) noMatchCallbacks.splice(idx, 1);
  };

  return {
    registerContents,
    registerEpubCfiRange,
    unregisterEpubCfiRange,
    registerElement,
    unregisterAllElements,
    registerNoMatchCallback,
    unregisterNoMatchCallback,
  };
};

export default useClickHandler;
